import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Pagination
    currentPage: 1,
    pageSize: 25,
    maxPages: 1,
    totalCount: 0,

    // Sorting
    sortBy: null,
    sortDirection: null,

    // Search
    searchValue: '',
    keyword: '',

    // Tab state
    pageTab: 'users',
    usersCount: 0,
    adminsCount: 0,
    reportsCount: 0,
    blockedCount: 0,

    // Selection
    checkedItems: [],

    // Detail panel
    detailPanelOpen: false,
    selectedUser: null,

    // Edit panel
    editModalOpen: false,
    editedUser: null,

    // Message modal
    messageModalOpen: false,
    messageUser: null,

    // Refresh trigger
    needRefresh: false,

    // Filters
    filters: {
        dateFrom: '',
        dateTo: '',
        gender: '',
        status: '',
    },
};

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setPageSize: (state, action) => {
            state.checkedItems = [];
            state.pageSize = action.payload;
            state.currentPage = 1;
        },
        setMaxPages: (state, action) => {
            state.maxPages = action.payload;
        },
        setTotalCount: (state, action) => {
            state.totalCount = action.payload;
        },
        setSorting: (state, action) => {
            const { sortBy, sortDirection } = action.payload;
            state.sortBy = sortBy;
            state.sortDirection = sortDirection;
        },
        setSearchValue: (state, action) => {
            state.searchValue = action.payload;
        },
        setKeyword: (state, action) => {
            state.keyword = action.payload;
        },
        setPageTab: (state, action) => {
            state.pageTab = action.payload;
            state.currentPage = 1;
            state.checkedItems = [];
        },
        setTabCounts: (state, action) => {
            const { usersCount, adminsCount, reportsCount, blockedCount } = action.payload;
            if (usersCount !== undefined) state.usersCount = usersCount;
            if (adminsCount !== undefined) state.adminsCount = adminsCount;
            if (reportsCount !== undefined) state.reportsCount = reportsCount;
            if (blockedCount !== undefined) state.blockedCount = blockedCount;
        },
        setCheckedItems: (state, action) => {
            state.checkedItems = action.payload;
        },
        toggleCheckedItem: (state, action) => {
            const id = action.payload;
            if (state.checkedItems.includes(id)) {
                state.checkedItems = state.checkedItems.filter(item => item !== id);
            } else {
                state.checkedItems.push(id);
            }
        },
        clearCheckedItems: (state) => {
            state.checkedItems = [];
        },
        toggleDetailPanel: (state) => {
            state.detailPanelOpen = !state.detailPanelOpen;
        },
        closeDetailPanel: (state) => {
            state.detailPanelOpen = false;
            state.selectedUser = null;
        },
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload;
            state.detailPanelOpen = true;
        },
        toggleEditModal: (state) => {
            state.editModalOpen = !state.editModalOpen;
        },
        openEditModal: (state) => {
            state.editModalOpen = true;
        },
        setEditedUser: (state, action) => {
            state.editedUser = action.payload;
        },
        openMessageModal: (state, action) => {
            state.messageModalOpen = true;
            state.messageUser = action.payload || null;
        },
        closeMessageModal: (state) => {
            state.messageModalOpen = false;
            state.messageUser = null;
        },
        toggleNeedRefresh: (state) => {
            state.needRefresh = !state.needRefresh;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
            state.currentPage = 1;
            state.checkedItems = [];
        },
        clearFilters: (state) => {
            state.filters = {
                dateFrom: '',
                dateTo: '',
                gender: '',
                status: '',
            };
            state.currentPage = 1;
            state.checkedItems = [];
        },
    },
});

export const {
    setPage,
    setPageSize,
    setMaxPages,
    setTotalCount,
    setSorting,
    setSearchValue,
    setKeyword,
    setPageTab,
    setTabCounts,
    setCheckedItems,
    toggleCheckedItem,
    clearCheckedItems,
    toggleDetailPanel,
    closeDetailPanel,
    setSelectedUser,
    toggleEditModal,
    openEditModal,
    setEditedUser,
    openMessageModal,
    closeMessageModal,
    toggleNeedRefresh,
    setFilters,
    clearFilters,
} = usersSlice.actions;

export default usersSlice.reducer;
